import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import { 
  Settings,
  Link,
  MessageSquare,
  FileText,
  Link2,
  Target,
  AlertCircle,
  Tag,
  Ban,
  Zap
} from 'lucide-react';
import { Client } from '@/types/client';
import BusinessRulesTabNavigation from '@/components/business-rules/BusinessRulesTabNavigation';
import IntegrationTab from '@/components/business-rules/IntegrationTab';
import ApiTab from '@/components/business-rules/ApiTab';
import BillingTab from '@/components/business-rules/BillingTab';
import { MessagesTab } from '@/components/business-rules/MessagesTab';
import { MetaApiTemplatesTab } from '@/components/business-rules/MetaApiTemplatesTab';
import EmailTemplatesManager from '@/components/email/EmailTemplatesManager';
import TemplateStrategiesTab from '@/components/business-rules/TemplateStrategiesTab';
import TagsTab from '@/components/business-rules/TagsTab';
import BlockedContactsTab from '@/components/business-rules/BlockedContactsTab';
import WebhooksTab from '@/components/business-rules/WebhooksTab';
import { apiService } from '@/lib/api';

const ClientBusinessRules: React.FC = () => {
  const { user } = useAuth();
  const [client, setClient] = useState<Client | null>(null);
  const [activeTab, setActiveTab] = useState('integration');
  const [messagesSubTab, setMessagesSubTab] = useState('oficial');

  // Se o canal de email do cliente estiver desabilitado e a sub-aba ativa for 'email', volta para 'oficial'.
  useEffect(() => {
    if (client && !client.email_canal_enabled && messagesSubTab === 'email') {
      setMessagesSubTab('oficial');
    }
  }, [client, messagesSubTab]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadClientData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('🔍 [ClientBusinessRules] Usuário logado:', user);
        console.log('🔍 [ClientBusinessRules] Client ID:', user?.client_id);
        
        if (!user?.client_id) {
          throw new Error('Usuário não possui client_id configurado');
        }

        // Buscar dados do cliente logado com normalização de payload
        console.log('🔍 [ClientBusinessRules] Fazendo requisição para:', `/clients/${user.client_id}`);
        const response = await apiService.get(`/clients/${user.client_id}`) as any;
        console.log('✅ [ClientBusinessRules] Dados do cliente recebidos:', response);

        const resolveClient = (payload: any): Client | null => {
          if (!payload) return null;
          if ((payload as any).id && (payload as any).legal_name) return payload as Client;
          if ((payload as any).client) return (payload as any).client as Client;
          if ((payload as any).data && (payload as any).data.client) return (payload as any).data.client as Client;
          if ((payload as any).data && (payload as any).data.id) return (payload as any).data as Client;
          return null;
        };

        const resolved = resolveClient(response);
        if (!resolved) {
          throw new Error('Estrutura de resposta inesperada ao carregar cliente');
        }
        setClient(resolved);
      } catch (error) {
        console.error('❌ [ClientBusinessRules] Erro ao carregar dados do cliente:', error);
        setError(error instanceof Error ? error.message : 'Erro desconhecido ao carregar dados do cliente');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadClientData();
    } else {
      console.log('⏳ [ClientBusinessRules] Aguardando usuário...');
      setLoading(false);
    }
  }, [user]);

  const tabs = [
    { value: 'integration', label: 'Integração', icon: Link2 },
    { value: 'api', label: 'API de envio', icon: Settings },
    { value: 'webhooks', label: 'Webhooks', icon: Zap },
    { value: 'messages', label: 'Mensagens', icon: MessageSquare },
    { value: 'tags', label: 'Tags', icon: Tag },
    { value: 'template-strategies', label: 'Estratégias', icon: Target },
    { value: 'billing', label: 'Automação', icon: FileText },
    { value: 'blocked-contacts', label: 'Contatos Bloqueados', icon: Ban },
  ];

  // Se ainda está carregando o usuário
  if (!user) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <AtomicLoadingScreen message="Carregando usuário..." />
        </div>
      </Layout>
    );
  }

  // Se está carregando dados do cliente
  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <AtomicLoadingScreen message="Carregando regras de negócio..." />
        </div>
      </Layout>
    );
  }

  // Se há erro
  if (error) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center max-w-md mx-auto">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Erro ao carregar</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <div className="bg-gray-50 p-4 rounded-lg text-left">
              <p className="text-sm text-gray-600 mb-2"><strong>Debug Info:</strong></p>
              <p className="text-xs text-gray-500">User ID: {user.id}</p>
              <p className="text-xs text-gray-500">User Role: {user.role}</p>
              <p className="text-xs text-gray-500">Client ID: {user.client_id || 'Não definido'}</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Se não encontrou o cliente
  if (!client) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center max-w-md mx-auto">
            <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Cliente não encontrado</h2>
            <p className="text-gray-600 mb-4">Não foi possível carregar as informações do cliente.</p>
            <div className="bg-gray-50 p-4 rounded-lg text-left">
              <p className="text-sm text-gray-600 mb-2"><strong>Debug Info:</strong></p>
              <p className="text-xs text-gray-500">User ID: {user.id}</p>
              <p className="text-xs text-gray-500">User Role: {user.role}</p>
              <p className="text-xs text-gray-500">Client ID: {user.client_id || 'Não definido'}</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                <Target className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-montserrat font-bold text-gray-900">
                  Regras de Negócio
                </h1>
                <p className="text-lg text-gray-600">
                  {client.legal_name} - {client.trade_name}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Card className="shadow-lg border-0">
            <CardHeader className="border-b border-gray-100 pb-6">
              <CardTitle className="text-2xl font-montserrat font-semibold text-gray-900">
                Configurações e Integrações
              </CardTitle>
              <CardDescription className="text-gray-600">
                Gerencie as configurações de integração, templates de mensagem e regras de negócio do seu sistema.
              </CardDescription>
            </CardHeader>
            
            <CardContent className="p-0">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                {/* Navigation tabs */}
                <div className="border-b border-gray-200 px-6 pt-6">
                  <BusinessRulesTabNavigation activeTab={activeTab} tabs={tabs} />
                </div>

                {/* Tab content */}
                <div className="p-6">
                  {activeTab === 'integration' && (
                    <IntegrationTab client={client} />
                  )}
                  
                  {activeTab === 'api' && (
                    <ApiTab client={client} />
                  )}
                  
                  {activeTab === 'webhooks' && (
                    <WebhooksTab client={client} />
                  )}
                  
                  {activeTab === 'billing' && (
                    <BillingTab client={client} />
                  )}
                  
                  {activeTab === 'messages' && (
                    <div className="space-y-6">
                      <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8">
                          <button
                            onClick={() => setMessagesSubTab('oficial')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                              messagesSubTab === 'oficial'
                                ? 'border-primary-500 text-primary-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            Templates API Oficial
                          </button>
                          <button
                            onClick={() => setMessagesSubTab('extra_oficial')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                              messagesSubTab === 'extra_oficial'
                                ? 'border-primary-500 text-primary-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            Templates API Extra Oficial
                          </button>
                          {client?.email_canal_enabled && (
                            <button
                              onClick={() => setMessagesSubTab('email')}
                              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                                messagesSubTab === 'email'
                                  ? 'border-primary-500 text-primary-600'
                                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                              }`}
                            >
                              Templates de Email
                            </button>
                          )}
                        </nav>
                      </div>
                      
                      {messagesSubTab === 'oficial' && (
                        <MetaApiTemplatesTab clientId={client.id} />
                      )}
                      
                      {messagesSubTab === 'extra_oficial' && (
                        <MessagesTab clientId={client.id} />
                      )}

                      {messagesSubTab === 'email' && client?.email_canal_enabled && (
                        <EmailTemplatesManager clientId={client.id} />
                      )}
                    </div>
                  )}
                  
                  {activeTab === 'template-strategies' && (
                    <TemplateStrategiesTab client={client} />
                  )}
                  
                  {activeTab === 'tags' && (
                    <TagsTab client={client} />
                  )}
                  
                  {activeTab === 'blocked-contacts' && (
                    <BlockedContactsTab client={client} />
                  )}
                </div>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default ClientBusinessRules;
